<script setup>
import { useRouteQuery } from '@vueuse/router';
import { stripBlockTags } from '@/composables/helpers/html';
import { getActivities } from '@/composables/builder/getActivities';
import { useActivityFavorites } from '@/composables/useActivityFavorites';
import { useHeroAltLogo } from '@/composables/useHeroAltLogo';
import { filterActivities, ACTIVITY_TYPES } from '~~/shared/utilities/activities/filter';
// The subdir auto-import name for this component collapses to
// `TwoSixActivitiesActivityCard`, not `ActivityCard` — import explicitly so
// it resolves both in the real app and in component tests.
import ActivityCard from './activity-card.vue';

const props = defineProps({
    headline: { type: String, default: '' },
    subHead: { type: String, default: '' },
    copy: { type: String, default: '' },
    treatAsHero: { type: Boolean, default: true },
    anchorId: { type: String, default: 'activities' },
});

// Fetch the Builder feed once during SSR; serialize + filter client-side.
const { data: activities } = useAsyncData('activities-feed', () => getActivities(), { default: () => [] });

const { favorites, loadFavorites, isFavorited, toggleFavorite } = useActivityFavorites();
onMounted(() => { loadFavorites(); });
const { loggedIn } = useUserSession();

// URL-backed view/filter state (shareable + deep-linkable).
const tab = useRouteQuery('tab', 'all');           // 'all' | 'favorites'
const type = useRouteQuery('type', 'all');

const typeOptions = computed(() => [
    { label: 'All Types', value: 'all' },
    ...ACTIVITY_TYPES.map((t) => ({ label: t, value: t })),
]);

// Mobile drives the same `tab` state through a native select instead of the two
// side-by-side tabs — the Agenda's mobile pattern. At 390px the pair doesn't fit:
// each label wraps to three lines inside its own button (measured 84px tall), and
// it only gets worse on a 375px phone.
const tabOptions = [
    { label: 'All Activities', value: 'all' },
    { label: 'My Favorites', value: 'favorites' },
];
const activeTabLabel = computed(
    () => tabOptions.find((t) => t.value === tab.value)?.label ?? tabOptions[0].label,
);

const filtered = computed(() =>
    filterActivities(activities.value || [], {
        tab: tab.value,
        type: type.value,
        favoriteIds: favorites.value,
    }),
);
const resultCount = computed(() => filtered.value.length);

// Same rule as the Agenda's showFilterBar (agenda/agenda.vue): drop the filter
// and the results count while My Favorites is showing its login or
// nothing-favorited state, since there's nothing there to filter. An empty
// result that the FILTER caused keeps them — hiding the control that stranded
// the user is how they get stuck with no way back.
const showFilterBar = computed(
    () => !(tab.value === 'favorites' && (!loggedIn.value || favorites.value.length === 0)),
);

// Progressive render ("infinite scroll like the Agenda").
const PAGE = 12;
const visibleCount = ref(PAGE);
watch([tab, type], () => { visibleCount.value = PAGE; });
const visible = computed(() => filtered.value.slice(0, visibleCount.value));
const hasMore = computed(() => visibleCount.value < filtered.value.length);

const sentinel = ref(null);
let observer;
onMounted(() => {
    observer = new IntersectionObserver((entries) => {
        if (entries[0]?.isIntersecting && hasMore.value) visibleCount.value += PAGE;
    });
    if (sentinel.value) observer.observe(sentinel.value);
});
onBeforeUnmount(() => observer?.disconnect());

// Deep-link: nav links to ...?tab=favorites#<anchorId>. On mount, if the
// favorites tab is pre-selected via the URL, scroll the block into view.
const root = ref(null);
// Dark "alt" header logo when this block is the page hero (index-0 Builder block
// or a standalone page). The composable no-ops when the block sits lower on the
// page. Same wiring as agenda/sessions/archive.
useHeroAltLogo(root);
onMounted(() => {
    if (tab.value === 'favorites') nextTick(() => root.value?.scrollIntoView({ behavior: 'smooth' }));
});

// Static src on an <img> makes Vite try to resolve the path as a build-time
// import and fail on Workers — image paths go through a const + :src binding.
const heroShardPng = '/img/2026/experience-hero-shard.png';

const clearFilters = () => { type.value = 'all'; };
const goLogin = () => navigateTo('/login');

// Tab treatment is lifted verbatim from the Agenda's tab toggle
// (agenda/agenda.vue) so the two pages stay identical: 4%-of-font-size dotted
// underline at a 7px offset, and the deselected tab picks up the same
// treatment on hover.
const TAB_SELECTED =
    'text-(--magenta) underline decoration-(--darker-pink) decoration-dotted decoration-[4%] underline-offset-[7px]';
const TAB_DESELECTED =
    'text-(--darker-pink) no-underline hover:text-(--magenta) hover:underline hover:decoration-(--darker-pink) hover:decoration-dotted hover:decoration-[4%] hover:underline-offset-[7px]';
</script>

<template>
    <section :id="anchorId" ref="root" class="activities relative overflow-clip bg-(--pink)" :class="{ 'is-hero': treatAsHero }">
        <!-- Shard decoration, exported from the Experience design (Figma node
             5621:59058) so this page reads differently from the Agenda. The
             export bakes in a #FCC3DC background that matches --pink exactly,
             so it composites against the section with no visible edge.

             Shown from 1220px up, not from lg (1024). The shard is anchored
             right and a fixed 678px, while the headline is a single fixed-size
             line ending at a constant x — so narrowing the viewport walks the
             art leftward into type that never reflows to make room. Measured on
             the orange itself (the PNG's pink field is not the art): within the
             headline's own vertical band the orange starts at
             `viewport - 0.375 * 678`, which crosses the headline's right edge
             below 1196px — 172px of overlap by 1024. Design only specs 1440.
             1220 is 1196 plus a 24px gutter, and it keeps the shard at the
             1231px width QA reviewed. Clearing the headline at 1024 instead
             would mean a ~155px shard, 23% of its design size, which reads as a
             bug rather than a decision; hiding it there matches mobile. The
             body copy never collides at any width. -->
        <img
            v-if="treatAsHero"
            :src="heroShardPng"
            alt=""
            aria-hidden="true"
            class="hero-shard hidden min-[1220px]:display-block absolute top-0 right-0 w-[678px] h-auto pointer-events-none z-0"
        />

        <div class="relative z-1 px-(--box-padding-lr-m) lg:px-(--box-padding-lr)">
            <!-- 30px on mobile, matching .block-sessions-agenda's header (measured:
                 the Agenda uses mb-[30px] lg:mb-[60px]). Was 48px, which read as a
                 gulf between the headline and the tabs next to the Agenda. -->
            <header class="activities__head mb-[30px] lg:mb-[60px]">
                <MessHtml v-if="headline" tag="h2" class="font-hubspot-serif font-normal text-[42px] lg:text-[72px] leading-[1.1] text-(--magenta) mb-4" :html="stripBlockTags(headline)" />
                <p v-if="subHead" class="activities__subhead font-hubspot-sans font-light text-[16px] leading-[26px] text-(--magenta) mb-2">{{ subHead }}</p>
                <div v-if="copy" class="activities__copy font-hubspot-sans font-light text-[16px] leading-[26px] text-(--magenta) max-w-[670px]" v-html="copy"></div>
            </header>

            <!-- 35px on mobile, matching the Agenda's rhythm (measured: its controls
                 row carries mb-[35px]). One gap drives every band here — headline to
                 tabs, tabs to controls, controls to cards — so the three separate QA
                 tickets about "this space" were all this single value at 48px.
                 Desktop keeps 48px; the tickets are mobile-only. -->
            <div class="flex flex-col gap-[35px] lg:gap-[48px]">
                <div class="activities__tabs flex items-end justify-between w-full">
                    <!-- Mobile: the tab state as a native select, layered invisibly over
                         a styled label so the OS picker does the work. Lifted from the
                         Agenda's narrow-phone control (agenda/agenda.vue) down to the
                         34px serif label and the chevron, per "use the same text sizing
                         and styling as the agenda". -->
                    <label
                        data-test="tab-select"
                        class="lg:hidden relative flex items-center gap-[10px] cursor-pointer w-fit font-hubspot-serif font-normal text-[34px] leading-none text-(--magenta) whitespace-nowrap">
                        <span>{{ activeTabLabel }}</span>
                        <ChevronDown class="w-[13px] h-[12px] shrink-0" />
                        <select
                            v-model="tab"
                            aria-label="Select activities view"
                            class="absolute inset-0 opacity-0 cursor-pointer appearance-none w-full h-full">
                            <option v-for="t in tabOptions" :key="t.value" :value="t.value">{{ t.label }}</option>
                        </select>
                    </label>

                    <!-- role=tablist lives here, not on the wrapper: on mobile the
                         wrapper holds a select, and a tablist whose only child is a
                         combobox is a lie to assistive tech. -->
                    <div class="hidden lg:flex gap-[41px] items-center font-hubspot-serif font-normal text-[28px] lg:text-[42px] leading-none" role="tablist">
                        <button
                            type="button"
                            role="tab"
                            :aria-selected="tab === 'all'"
                            class="bg-transparent border-none p-0 cursor-pointer transition-colors duration-200"
                            :class="tab === 'all' ? TAB_SELECTED : TAB_DESELECTED"
                            @click="tab = 'all'"
                        >All Activities</button>
                        <button
                            type="button"
                            role="tab"
                            :aria-selected="tab === 'favorites'"
                            class="bg-transparent border-none p-0 cursor-pointer transition-colors duration-200"
                            :class="tab === 'favorites' ? TAB_SELECTED : TAB_DESELECTED"
                            @click="tab = 'favorites'"
                        >My Favorites</button>
                    </div>
                </div>

                <!-- Row on mobile too, not stacked. The filter was full-width, which
                     pushed the results count onto its own line (UNB). The select is
                     sized to its longest option ("Boundless Connections", ~190px of
                     text plus 64px of padding and 2px of border) rather than to the
                     container, which leaves room for the count beside it: at 430px
                     that is 270 + 20 + the count inside 382px. flex-wrap is the
                     safety valve — below about 400px the pair no longer fits, and
                     wrapping puts the count back on its own line rather than
                     overflowing the card. -->
                <!-- 23px between the two dropdowns on mobile, not the shared 35px
                     (UNB-661). The tab select above carries its own padding, so an
                     identical 35px reads as more whitespace here than the equivalent
                     gap on the Agenda. Applied to this row only: the gap below it,
                     down to the cards, stays 35px (UNB-662). -->
                <div v-if="showFilterBar" class="activities__controls flex flex-row flex-wrap items-center justify-between w-full gap-[20px] -mt-[12px] lg:mt-0">
                    <div class="flex flex-row gap-[20px] items-center">
                        <div class="relative w-auto">
                            <select
                                v-model="type"
                                aria-label="Filter by type"
                                class="appearance-none w-[270px] max-w-full rounded-[50px] border border-(--magenta) bg-transparent px-[20px] py-[10px] pr-[44px] font-hubspot-serif font-normal text-[17px] tracking-[0.34px] text-(--magenta) cursor-pointer"
                            >
                                <option v-for="o in typeOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
                            </select>
                            <svg class="pointer-events-none absolute right-[20px] top-1/2 -translate-y-1/2 text-(--magenta)" width="9" height="5" viewBox="0 0 10.25 6.25" fill="none" aria-hidden="true">
                                <path d="M0.625 0.625L5.125 5.625L9.625 0.625" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                    </div>
                    <p class="activities__count font-hubspot-sans font-normal text-[12px] tracking-[0.84px] uppercase text-(--darker-pink) text-right whitespace-nowrap">{{ resultCount }} RESULTS</p>
                </div>

                <!-- My Favorites, logged out: mirrors the My Schedule login wall. -->
                <!-- Fixed padding, NOT min-h-[50vh]. A vh min-height is exactly the
                     "top and bottom spacing flexes with the browser size" QA flagged:
                     on a tall window it ballooned, on a short one it collapsed. These
                     are the Agenda's own empty-state values (py-[60px] lg:py-[90px]),
                     and -mb cancels the section's own 24px so the bottom reads as the
                     60px asked for rather than 84px. -->
                <div v-if="tab === 'favorites' && !loggedIn" class="empty flex flex-col items-center justify-center py-[60px] lg:py-[90px] -mt-[35px] -mb-[24px] lg:mt-0 lg:mb-0">
                    <!-- Size comes from the scoped .activities .empty h2 rule below,
                         not a utility class here: a global `.empty h2` outranks a
                         single Tailwind class, so `text-[30px]` silently lost. -->
                    <h2>You must be logged in to view <span>My Favorites</span></h2>
                    <p>
                        In order to favorite activities, you must have <NuxtLink to="/register">purchased registration</NuxtLink> for UNBOUND and you must be logged in. Once logged in, simply click Add to My Favorites on any activity to save it here.
                    </p>
                    <div class="actions">
                        <button type="button" class="ms-cta-outline" @click="goLogin">Login</button>
                        <button type="button" class="ms-cta-outline" @click="tab = 'all'">View All Activities</button>
                    </div>
                </div>

                <!-- My Favorites, logged in with nothing favorited yet (no login button). -->
                <div v-else-if="tab === 'favorites' && favorites.length === 0" class="empty flex flex-col items-center justify-center py-[60px] lg:py-[90px] -mt-[35px] -mb-[24px] lg:mt-0 lg:mb-0">
                    <h2>You haven't favorited any activities yet</h2>
                    <p>
                        Browse the activities and click Add to My Favorites on any of them to save them here, so you can easily find your must-do experiences during UNBOUND.
                    </p>
                    <div class="actions">
                        <button type="button" class="ms-cta-outline" @click="tab = 'all'">View All Activities</button>
                    </div>
                </div>

                <!-- Filters exclude everything (either tab). Structured like the
                     Agenda's no-results state — .empty with an h2, a line of copy and
                     an action — rather than the one-line paragraph it used to be, which
                     didn't read as a state at all next to the Agenda. Copy is QA's,
                     verbatim on the favorites side; the all-tab wording mirrors it,
                     since QA only specified the favorites case they were looking at. -->
                <div v-else-if="resultCount === 0" class="activities__empty empty flex flex-col items-center justify-center py-[60px] lg:py-[90px] -mt-[35px] -mb-[24px] lg:mt-0 lg:mb-0">
                    <h2>
                        {{ tab === 'favorites'
                            ? 'None of your favorited activities match these filters.'
                            : 'None of the activities match these filters.' }}
                    </h2>
                    <p>
                        {{ tab === 'favorites'
                            ? 'Clear your filters to see all of your favorited activities'
                            : 'Clear your filters to see all activities' }}
                    </p>
                    <div class="actions">
                        <button type="button" class="ms-cta-outline" @click="clearFilters">Clear filters</button>
                    </div>
                </div>

                <!-- 35px above the cards, the shared value — QA confirmed this gap was
                     already right (UNB-662) and an earlier pass moved the 23px here by
                     mistake. The 23px belongs one band up, between the two dropdowns. -->
                <div v-else class="activities__grid grid grid-cols-1 lg:grid-cols-3 gap-[20px]">
                    <ActivityCard
                        v-for="a in visible"
                        :key="a.id"
                        :activity="a"
                        :favorited="isFavorited(a.id)"
                        @toggle="toggleFavorite(a.id)"
                    />
                </div>
            </div>

            <!-- Only when there are cards to scroll back over. On the login / empty /
                 no-results states there is nothing to return to, and its 36px plus
                 2×38px margins would just stack dead space under the copy — the same
                 reason the Agenda gates its own on `showList`. -->
            <GlobalBackToTop v-if="visible.length" />
        </div>

        <div ref="sentinel" class="activities__sentinel h-px" aria-hidden="true"></div>
    </section>
</template>

<style scoped>
/* Vertical padding lives on the section root (like agenda/agenda.vue) so the
   hero variant can override just the top without fighting Tailwind's utilities
   layer. */
.activities {
    /* Stays 24px on mobile, deliberately — matching .block-sessions-agenda.
       QA asked for "the standard 60px" at the bottom (UNB-658) when this was
       24px with nothing under the grid. The back-to-top added since (UNB-663)
       carries a 38px bottom margin, so 24 + 38 = 62px below the button, which
       is exactly what the Agenda produces. Raising this to 60 would make it
       99px — more space than the page it is supposed to match. */
    padding-block: var(--box-padding-tb-m);
}

@media (min-width: 1024px) {
    .activities {
        padding-block: var(--box-padding-tb);
        /* Same token as mobile above, and for the same reason (UNB-663, third
           pass): 90px here put the button 129px above the block edge against the
           Agenda's 62px. .block-sessions-agenda declares `padding: 75px 0 28px`
           on desktop, but its own .is-hero variant then resets the bottom to
           var(--box-padding-tb-m) — so the Agenda actually renders 24px, and
           24 + the button's 38px margin is that 62px. Matching the token rather
           than the 28px in the declaration keeps the two pages equal. */
        padding-bottom: var(--box-padding-tb-m);
    }
}

/* Back-to-top positioning, matching .block-sessions-agenda's. The component is
   shared, but the offsets are per-block: the fixed/offscreen states pin the
   button to the page gutter, and the resting state drops it flush at the end of
   the list. */
.activities .back-to-top {
    min-height: 36px;
    margin-top: 2.375rem;
    margin-bottom: 2.375rem;
}

/* :deep() is required on every rule that reaches the BUTTON. This block is
   scoped, so it reaches .back-to-top itself (the child component's root element)
   but nothing inside it — which is why the margins above landed while the button
   offsets silently didn't, leaving it 38px right and 38px high of where the
   Agenda parks it (UNB-663, second pass). agenda.vue carries the same rules and
   gets away without :deep() only because its style block is unscoped. */
.activities :deep(.back-to-top.is-fixed > button),
.activities :deep(.back-to-top.offscreen > button) {
    left: var(--box-padding-lr-m);
}

@media (min-width: 1024px) {
    .activities :deep(.back-to-top.is-fixed > button),
    .activities :deep(.back-to-top.offscreen > button) {
        left: var(--box-padding-lr);
    }
}

.activities :deep(.back-to-top:not(.is-fixed):not(.offscreen) > button) {
    left: 0;
    bottom: 0;
    transition-property: opacity, transform, background-color, color;
}

/* Empty-state headline: match the Agenda's parallel "You must be logged in to
   view My Schedule" message, measured at 30px/39px there while this block
   rendered 36px/43.2px.

   This has to be a rule, not a utility class on the <h2>. A global `.empty h2`
   (class + element) outranks a single Tailwind class, so `text-[30px]` applied
   nothing and the heading stayed 36px — verified by measuring, not assumed.
   Scoping it under .activities keeps every other page's .empty untouched. */
.activities .empty h2 {
    font-size: 30px;
    line-height: 39px;
}

/* Hero variant: pull the pink up under the (fixed) nav and pad the top so the
   nav doesn't overlap the headline. Same values as .block-sessions-agenda.is-hero. */
.activities.is-hero {
    margin-top: calc(var(--header-height) * -1);
    padding-top: 97px;
}

@media (min-width: 1024px) {
    .activities.is-hero {
        padding-top: 150px;
    }
}

/* My Favorites empty/login states — mirrors the My Schedule empty state in
   agenda/agenda.vue so the two features read the same. */
.empty h2 {
    text-align: center;
    font-family: var(--font-hubspot-serif);
    font-size: 2.25rem;
    font-weight: 400;
    letter-spacing: 0;
    margin-bottom: 1.875rem;
    max-width: 56.25rem;
    line-height: 1.2;
    color: var(--magenta);
}

.empty h2 span {
    color: var(--magenta);
}

.empty p {
    text-align: center;
    margin-bottom: 1.875rem;
    max-width: 52rem;
    font-family: var(--font-hubspot-sans);
    font-weight: 300;
    font-size: 0.9375rem;
    line-height: 1.4375rem;
    letter-spacing: 0.3px;
    color: var(--magenta);
}

/* Inline text links — the block's Builder body copy and the empty-state copy
   share the standard 2026 text-link treatment. Thickness and offset are
   percentages so they track whatever font-size the surrounding copy uses. */
.activities__copy :deep(a),
.empty p :deep(a) {
    color: var(--magenta);
    text-decoration-line: underline;
    text-decoration-style: dotted;
    text-decoration-skip-ink: auto;
    text-decoration-color: var(--darker-pink);
    text-decoration-thickness: 8.5%;
    text-underline-offset: 25%;
}

@media (hover: hover) {
    .activities__copy :deep(a):hover,
    .empty p :deep(a):hover {
        text-decoration: none;
        color: var(--purple);
    }
}

.empty .actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 1.5625rem;
}

.ms-cta-outline {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border-radius: 50px;
    border: 1px solid var(--magenta);
    padding: 10px 20px;
    font-family: var(--font-hubspot-serif);
    font-size: calc(17 / var(--base-fs) * 1rem);
    line-height: 1.4;
    letter-spacing: 0.34px;
    background: transparent;
    color: var(--magenta);
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

@media (hover: hover) {
    .ms-cta-outline:hover {
        background: var(--magenta);
        color: #fff;
    }
}
</style>
